import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FilenameInput } from "@/components/ui/FilenameInput";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState, useRef } from "react";
import { Loader2 } from "lucide-react";
import { apiErrorMessage, apiValidationFieldErrors, backendClient } from "@/lib/api-client";
import { useParams } from "@tanstack/react-router";
import { toast } from "react-hot-toast";

const INVALID_FILENAME_CHARS = /[/\\:*?"<>|]/;

/** Split a filename into stem and extension using the last dot. */
function splitNameExtension(name: string): { stem: string; extension: string } {
  const dotIndex = name.lastIndexOf(".");
  if (dotIndex <= 0) return { stem: name, extension: "" };
  return { stem: name.substring(0, dotIndex), extension: name.substring(dotIndex) };
}

type FormValues = { stem: string };

interface RenameDocumentDialogProps {
  row: { id: string; displayName?: string; fileName?: string };
  onSuccess?: () => void;
  isOpen: boolean;
  onClose: () => void;
}

export function RenameDocumentDialog({ row, onSuccess, isOpen, onClose }: RenameDocumentDialogProps) {
    const { slug } = useParams({ from: '/matter/$slug' });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const currentName = row.displayName || row.fileName || "";
    const { stem: currentStem, extension } = splitNameExtension(currentName);

    const stemSchema = z.object({
      stem: z
        .string()
        .min(1, "Name cannot be empty")
        .refine((val) => val.trim().length > 0, { message: "Name cannot be empty" })
        .refine((val) => (val.trim() + extension).length <= 255, {
          message: "Name must be 255 characters or fewer",
        })
        .refine((val) => !INVALID_FILENAME_CHARS.test(val), {
          message: 'Name contains invalid characters: / \\ : * ? " < > |',
        }),
    });

    const form = useForm<FormValues>({
      resolver: zodResolver(stemSchema),
      defaultValues: {
        stem: currentStem,
      },
    });

    const watchedStem = form.watch("stem");
    const hasChanges = watchedStem.trim() !== currentStem;

    const onSubmit = async (data: FormValues) => {
      const trimmedStem = data.stem.trim();
      const newDisplayName = trimmedStem + extension;
      if (newDisplayName === currentName) {
        onClose();
        return;
      }

      try {
        setIsSubmitting(true);
        await backendClient.patch(`/correspondence/${slug}/file-references/${row.id}`, {
          display_name: newDisplayName,
        });

        toast.success("Document renamed");
        onSuccess?.();
        onClose();
      } catch (error: unknown) {
        const status = (error as { response?: { status?: unknown } })?.response?.status;
        if (status === 409) {
          form.setError("stem", {
            message: "A file with this name already exists in the same folder",
          });
        } else {
          console.error("Error renaming document:", error);
          const fieldError = apiValidationFieldErrors(error)[0];
          if (fieldError) {
            form.setError("stem", { message: fieldError.message });
          } else {
            toast.error(apiErrorMessage(error) || "Failed to rename document. Please try again.");
          }
        }
      } finally {
        setIsSubmitting(false);
      }
    };

    return (
      <Dialog
        open={isOpen}
        onOpenChange={(newOpen) => {
          if (!newOpen) {
            form.reset({ stem: currentStem });
            onClose();
          }
        }}
      >
        <DialogContent
          className="sm:max-w-md"
          onInteractOutside={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            setTimeout(() => {
              const input = inputRef.current;
              if (input) {
                input.focus();
                // Place cursor at the end of the text
                const len = input.value.length;
                input.setSelectionRange(len, len);
              }
            }, 0);
          }}
        >
          <DialogHeader>
            <DialogTitle>Rename Document</DialogTitle>
            <DialogDescription>Enter a new name for this document.</DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4 pt-4">
              <FormField
                name="stem"
                control={form.control}
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-2 space-y-0">
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <FilenameInput
                        {...field}
                        ref={(e) => {
                          field.ref(e);
                          (inputRef as React.MutableRefObject<HTMLInputElement | null>).current = e;
                        }}
                        extension={extension}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter>
                <div className="flex flex-row gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onClose();
                    }}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isSubmitting || !hasChanges} className="flex items-center gap-2">
                    {isSubmitting ? (
                      <>
                        <span>Renaming...</span>
                        <Loader2 className="w-4 h-4 animate-spin" />
                      </>
                    ) : (
                      "Rename"
                    )}
                  </Button>
                </div>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    );
}
